// UAD 2.6/3.6 Mapping Utilities - NEW ADDITION
// This file handles UAD standardization without affecting existing functionality

export const UAD_VERSIONS = {
  UAD_26: '2.6',
  UAD_36: '3.6'
};

// UAD 2.6/3.6 Required Fields Mapping
export const UAD_FIELD_MAPPINGS = {
  // Subject Property Fields
  subjectProperty: {
    propertyAddress: 'PropertyAddress',
    city: 'City',
    state: 'State',
    zipCode: 'ZipCode',
    county: 'County',
    censusTrack: 'CensusTrack',
    mapReference: 'MapReference',
    propertyRights: 'PropertyRights',
    assignmentType: 'AssignmentType',
    intendedUser: 'IntendedUser',
    intendedUse: 'IntendedUse',
    effectiveDate: 'EffectiveDate',
    dateOfInspection: 'DateOfInspection',
    legalDescription: 'LegalDescription',
    neighborhood: 'Neighborhood',
    occupancy: 'Occupancy',
    propertyType: 'PropertyType',
    designStyle: 'Design/Style',
    yearBuilt: 'YearBuilt',
    effectiveAge: 'EffectiveAge',
    totalRooms: 'TotalRooms',
    bedrooms: 'Bedrooms',
    bathrooms: 'Bathrooms',
    squareFootage: 'SquareFootage',
    lotSize: 'LotSize',
    view: 'View',
    condition: 'Condition',
    quality: 'Quality',
    functionalUtility: 'FunctionalUtility',
    heatingCooling: 'HeatingCooling',
    energyEfficient: 'EnergyEfficient',
    garage: 'Garage',
    porch: 'Porch',
    fence: 'Fence',
    pool: 'Pool',
    fireplace: 'Fireplace',
    woodstove: 'Woodstove',
    basement: 'Basement',
    finishedRoomsBelow: 'FinishedRoomsBelow',
    functionalUtilityRating: 'FunctionalUtilityRating',
    marketingTime: 'MarketingTime',
    listingHistory: 'ListingHistory',
    currentListing: 'CurrentListing',
    concessions: 'Concessions',
    salesComparison: 'SalesComparison'
  },

  // Comparable Fields
  comparable: {
    proximityToSubject: 'ProximityToSubject',
    salePrice: 'SalePrice',
    pricePerSqFt: 'PricePerSqFt',
    dateOfSale: 'DateOfSale',
    dataSource: 'DataSource',
    verification: 'Verification',
    valueAdjustments: 'ValueAdjustments',
    adjustedSalePrice: 'AdjustedSalePrice',
    comments: 'Comments'
  }
};

// UAD Standard Values and Codes
export const UAD_STANDARD_VALUES = {
  propertyRights: [
    'Fee Simple',
    'Leasehold',
    'Life Estate',
    'Other'
  ],

  assignmentType: [
    'Purchase Transaction',
    'Refinance Transaction',
    'Other'
  ],

  intendedUse: [
    'Purchase Transaction',
    'Refinance Transaction',
    'Other'
  ],

  occupancy: [
    'Owner',
    'Tenant',
    'Vacant'
  ],

  propertyType: [
    'Single Family',
    'Condominium',
    'Cooperative',
    'Manufactured Home',
    'PUD',
    '2-4 Family'
  ],

  condition: [
    'C1', 'C2', 'C3', 'C4', 'C5', 'C6'
  ],

  quality: [
    'Q1', 'Q2', 'Q3', 'Q4', 'Q5', 'Q6'
  ],

  view: [
    'Neutral',
    'Beneficial',
    'Adverse'
  ],

  functionalUtility: [
    'Typical',
    'Atypical'
  ],

  dataSource: [
    'Public Records',
    'MLS',
    'Appraiser',
    'Owner',
    'Other'
  ],

  verification: [
    'Public Records',
    'MLS',
    'Listing Agent',
    'Selling Agent',
    'Owner',
    'Appraiser',
    'Other'
  ]
};

// UAD 3.6 Additional Fields (not in 2.6)
export const UAD_36_ADDITIONAL_FIELDS = {
  energyEfficiencyItems: [
    'Solar Panels',
    'Energy Star Appliances',
    'High Efficiency HVAC',
    'Insulation',
    'Windows',
    'Other'
  ],

  floodZone: [
    'A', 'AE', 'AH', 'AO', 'AR', 'A99', 'V', 'VE', 'X', 'B', 'C', 'D'
  ],

  naturalHazards: [
    'Earthquake',
    'Flood',
    'Hurricane',
    'Tornado',
    'Wildfire',
    'Other',
    'None Known'
  ]
};

// Map internal property data to UAD format
export const mapToUADFormat = (propertyData, version = UAD_VERSIONS.UAD_36) => {
  if (!propertyData) return null;

  const uadData = {
    version: version,
    subjectProperty: {},
    metadata: {
      generatedDate: new Date().toISOString(),
      generatedBy: 'Property AI UAD Converter'
    }
  };

  // Map basic property fields
  const mapping = UAD_FIELD_MAPPINGS.subjectProperty;

  // Map common fields that exist in both internal and UAD format
  Object.keys(mapping).forEach(internalField => {
    const uadField = mapping[internalField];
    let value = null;

    // Handle different possible field names from API responses
    if (propertyData[internalField]) {
      value = propertyData[internalField];
    } else if (propertyData[internalField.charAt(0).toUpperCase() + internalField.slice(1)]) {
      value = propertyData[internalField.charAt(0).toUpperCase() + internalField.slice(1)];
    } else {
      // Try to map from ProMix API field names
      switch (internalField) {
        case 'bedrooms':
          value = propertyData.BedroomsTotal || propertyData.bedrooms;
          break;
        case 'bathrooms':
          value = propertyData.BathroomsDecimal || propertyData.bathrooms;
          break;
        case 'squareFootage':
          value = propertyData.LivingArea || propertyData.sqft || propertyData.squareFootage;
          break;
        case 'yearBuilt':
          value = propertyData.YearBuilt || propertyData.yearBuilt;
          break;
        case 'lotSize':
          value = propertyData.LotSizeSquareFeet || propertyData.lotSize;
          break;
        case 'propertyAddress':
          value = propertyData.UnparsedAddress || propertyData.address || propertyData.propertyAddress;
          break;
        case 'city':
          value = propertyData.City || propertyData.city;
          break;
        case 'state':
          value = propertyData.StateOrProvince || propertyData.state;
          break;
        case 'zipCode':
          value = propertyData.PostalCode || propertyData.zipCode;
          break;
        case 'county':
          value = propertyData.CountyOrParish || propertyData.county;
          break;
        default:
          value = propertyData[internalField];
      }
    }

    if (value !== null && value !== undefined) {
      uadData.subjectProperty[uadField] = value;
    }
  });

  // Add default UAD values for required fields if missing
  addDefaultUADValues(uadData.subjectProperty, version);

  return uadData;
};

// Map comparable data to UAD format
export const mapComparablesToUAD = (comparablesData, version = UAD_VERSIONS.UAD_36) => {
  if (!comparablesData || !Array.isArray(comparablesData)) return [];

  return comparablesData.map((comp, index) => {
    const compData = comp.rawData || comp;
    const uadComparable = {
      id: `Comp${index + 1}`,
      version: version
    };

    // Map comparable-specific fields
    const mapping = UAD_FIELD_MAPPINGS.comparable;
    Object.keys(mapping).forEach(internalField => {
      const uadField = mapping[internalField];
      let value = null;

      switch (internalField) {
        case 'salePrice':
          value = compData.ClosePrice || compData.salePrice || compData.price;
          break;
        case 'dateOfSale':
          value = compData.CloseDate || compData.dateOfSale;
          break;
        case 'proximityToSubject':
          value = comp.distance || compData.distance;
          break;
        case 'pricePerSqFt':
          const price = compData.ClosePrice || compData.salePrice;
          const sqft = compData.LivingArea || compData.sqft;
          value = (price && sqft) ? Math.round(price / sqft) : null;
          break;
        case 'dataSource':
          value = 'MLS'; // Default for ProMix data
          break;
        case 'verification':
          value = 'MLS'; // Default for ProMix data
          break;
        default:
          value = compData[internalField];
      }

      if (value !== null && value !== undefined) {
        uadComparable[uadField] = value;
      }
    });

    // Add subject property fields for comparison
    const subjectMapping = UAD_FIELD_MAPPINGS.subjectProperty;
    Object.keys(subjectMapping).forEach(internalField => {
      const uadField = subjectMapping[internalField];
      let value = null;

      switch (internalField) {
        case 'bedrooms':
          value = compData.BedroomsTotal || compData.bedrooms;
          break;
        case 'bathrooms':
          value = compData.BathroomsDecimal || compData.bathrooms;
          break;
        case 'squareFootage':
          value = compData.LivingArea || compData.sqft;
          break;
        case 'yearBuilt':
          value = compData.YearBuilt || compData.yearBuilt;
          break;
        case 'lotSize':
          value = compData.LotSizeSquareFeet || compData.lotSize;
          break;
        case 'propertyAddress':
          value = compData.UnparsedAddress || compData.address;
          break;
        default:
          value = compData[internalField];
      }

      if (value !== null && value !== undefined) {
        uadComparable[uadField] = value;
      }
    });

    return uadComparable;
  });
};

// Add default UAD values for required fields
const addDefaultUADValues = (uadData, version) => {
  // Required fields with defaults
  const defaults = {
    'PropertyRights': 'Fee Simple',
    'AssignmentType': 'Purchase Transaction',
    'IntendedUser': 'Lender/Client',
    'IntendedUse': 'Purchase Transaction',
    'Occupancy': 'Owner',
    'PropertyType': 'Single Family',
    'Condition': 'C4',
    'Quality': 'Q4',
    'View': 'Neutral',
    'FunctionalUtility': 'Typical'
  };

  Object.keys(defaults).forEach(field => {
    if (!uadData[field]) {
      uadData[field] = defaults[field];
    }
  });

  // UAD 3.6 specific defaults
  if (version === UAD_VERSIONS.UAD_36) {
    if (!uadData['FloodZone']) {
      uadData['FloodZone'] = 'X';
    }
    if (!uadData['NaturalHazards']) {
      uadData['NaturalHazards'] = 'None Known';
    }
  }
};

// Validate UAD compliance
export const validateUADCompliance = (uadData, version = UAD_VERSIONS.UAD_36) => {
  const errors = [];
  const warnings = [];

  // Required fields for UAD compliance
  const requiredFields = [
    'PropertyAddress',
    'City',
    'State',
    'ZipCode',
    'PropertyRights',
    'AssignmentType',
    'IntendedUser',
    'IntendedUse',
    'PropertyType',
    'YearBuilt',
    'Bedrooms',
    'Bathrooms',
    'SquareFootage',
    'Condition',
    'Quality'
  ];

  // Check required fields
  requiredFields.forEach(field => {
    if (!uadData.subjectProperty || !uadData.subjectProperty[field]) {
      errors.push(`Missing required UAD field: ${field}`);
    }
  });

  // Validate field values against UAD standards
  if (uadData.subjectProperty) {
    Object.keys(UAD_STANDARD_VALUES).forEach(field => {
      const uadField = UAD_FIELD_MAPPINGS.subjectProperty[field];
      const value = uadData.subjectProperty[uadField];

      if (value && !UAD_STANDARD_VALUES[field].includes(value)) {
        warnings.push(`Non-standard UAD value for ${uadField}: ${value}`);
      }
    });
  }

  // UAD 3.6 specific validations
  if (version === UAD_VERSIONS.UAD_36) {
    const uad36RequiredFields = ['FloodZone', 'NaturalHazards'];
    uad36RequiredFields.forEach(field => {
      if (!uadData.subjectProperty || !uadData.subjectProperty[field]) {
        warnings.push(`Missing UAD 3.6 recommended field: ${field}`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    compliance: {
      requiredFieldsPresent: requiredFields.length - errors.length,
      totalRequiredFields: requiredFields.length,
      compliancePercentage: Math.round(((requiredFields.length - errors.length) / requiredFields.length) * 100)
    }
  };
};

// Generate UAD XML output (simplified version)
export const generateUADXML = (uadData, comparables = [], version = UAD_VERSIONS.UAD_36) => {
  const xmlHeader = '<?xml version="1.0" encoding="UTF-8"?>\n';
  const rootElement = version === UAD_VERSIONS.UAD_36 ? 'AppraisalDataset_36' : 'AppraisalDataset_26';

  let xml = xmlHeader;
  xml += `<${rootElement} version="${version}">\n`;
  xml += '  <Subject>\n';

  // Add subject property data
  if (uadData.subjectProperty) {
    Object.keys(uadData.subjectProperty).forEach(field => {
      const value = uadData.subjectProperty[field];
      if (value !== null && value !== undefined) {
        xml += `    <${field}>${escapeXML(value.toString())}</${field}>\n`;
      }
    });
  }

  xml += '  </Subject>\n';

  // Add comparables
  if (comparables && comparables.length > 0) {
    xml += '  <Comparables>\n';
    comparables.forEach((comp, index) => {
      xml += `    <Comparable${index + 1}>\n`;
      Object.keys(comp).forEach(field => {
        if (field !== 'id' && field !== 'version') {
          const value = comp[field];
          if (value !== null && value !== undefined) {
            xml += `      <${field}>${escapeXML(value.toString())}</${field}>\n`;
          }
        }
      });
      xml += `    </Comparable${index + 1}>\n`;
    });
    xml += '  </Comparables>\n';
  }

  xml += `</${rootElement}>`;

  return xml;
};

// Helper function to escape XML characters
const escapeXML = (str) => {
  return str.replace(/[<>&'"]/g, (c) => {
    switch (c) {
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '&': return '&amp;';
      case "'": return '&apos;';
      case '"': return '&quot;';
      default: return c;
    }
  });
};

// Generate UAD narrative sections
export const generateUADNarrativeSections = (uadData, comparables = [], version = UAD_VERSIONS.UAD_36) => {
  const sections = {
    subjectPropertyDescription: generateSubjectPropertyNarrative(uadData),
    neighborhoodDescription: generateNeighborhoodNarrative(uadData),
    siteDescription: generateSiteNarrative(uadData),
    improvementsDescription: generateImprovementsNarrative(uadData),
    salesComparisonApproach: generateSalesComparisonNarrative(uadData, comparables),
    reconciliation: generateReconciliationNarrative(uadData, comparables)
  };

  // Add UAD 3.6 specific sections
  if (version === UAD_VERSIONS.UAD_36) {
    sections.energyEfficiency = generateEnergyEfficiencyNarrative(uadData);
    sections.environmentalConditions = generateEnvironmentalNarrative(uadData);
  }

  return sections;
};

// Helper functions for narrative generation
const generateSubjectPropertyNarrative = (uadData) => {
  const subject = uadData.subjectProperty || {};
  return `The subject property is located at ${subject.PropertyAddress || '[Address]'}, ${subject.City || '[City]'}, ${subject.State || '[State]'} ${subject.ZipCode || '[Zip]'}. The property is a ${subject.PropertyType || 'Single Family'} ${subject['Design/Style'] || 'style'} residence built in ${subject.YearBuilt || '[Year]'}. The improvements contain ${subject.SquareFootage || '[SF]'} square feet of living area with ${subject.Bedrooms || '[BR]'} bedrooms and ${subject.Bathrooms || '[BA]'} bathrooms.`;
};

const generateNeighborhoodNarrative = (uadData) => {
  const subject = uadData.subjectProperty || {};
  return `The subject property is located in the ${subject.Neighborhood || subject.City || '[Neighborhood]'} area. The neighborhood is characterized by ${subject.PropertyType || 'single family'} residences of similar vintage and quality. The area shows ${subject.View === 'Beneficial' ? 'positive' : subject.View === 'Adverse' ? 'negative' : 'neutral'} market conditions.`;
};

const generateSiteNarrative = (uadData) => {
  const subject = uadData.subjectProperty || {};
  return `The subject site contains ${subject.LotSize ? (subject.LotSize + ' square feet') : '[Lot Size]'} and appears to be ${subject.FunctionalUtility || 'typical'} in size for the area. The site has ${subject.View || 'neutral'} views and ${subject.Condition ? 'is in ' + subject.Condition + ' condition' : 'appears to be in average condition'}.`;
};

const generateImprovementsNarrative = (uadData) => {
  const subject = uadData.subjectProperty || {};
  return `The improvements are ${subject.Quality || 'average'} quality construction built in ${subject.YearBuilt || '[Year]'}. The condition is rated ${subject.Condition || 'C4'} and the effective age is estimated at ${subject.EffectiveAge || 'typical for year built'} years. The functional utility is considered ${subject.FunctionalUtility || 'typical'} for the market area.`;
};

const generateSalesComparisonNarrative = (uadData, comparables) => {
  const compCount = comparables ? comparables.length : 0;
  return `The Sales Comparison Approach utilized ${compCount} comparable sales that are similar to the subject in location, size, condition, and other relevant characteristics. Adjustments were made for differences in location, physical characteristics, and market conditions. The adjusted sale prices provide a range of value indications for the subject property.`;
};

const generateReconciliationNarrative = (uadData, comparables) => {
  return `Based on the analysis of comparable sales and market data, the indicated value by the Sales Comparison Approach is well supported. The comparables used are representative of the subject's market and required minimal adjustments. The final value conclusion is based on the weight given to the most similar and reliable comparable sales.`;
};

const generateEnergyEfficiencyNarrative = (uadData) => {
  const subject = uadData.subjectProperty || {};
  return `Energy efficiency features include ${subject.EnergyEfficient || 'typical systems and appliances for the age and type of construction'}. The property ${subject.EnergyEfficient ? 'includes energy-efficient improvements' : 'does not include any specific energy-efficient improvements beyond standard construction'}.`;
};

const generateEnvironmentalNarrative = (uadData) => {
  const subject = uadData.subjectProperty || {};
  return `The property is located in flood zone ${subject.FloodZone || 'X (minimal flood hazard)'}. Natural hazard risks for the area include ${subject.NaturalHazards || 'typical risks for the geographic region'}. No adverse environmental conditions were observed during the inspection.`;
};

export default {
  UAD_VERSIONS,
  UAD_FIELD_MAPPINGS,
  UAD_STANDARD_VALUES,
  UAD_36_ADDITIONAL_FIELDS,
  mapToUADFormat,
  mapComparablesToUAD,
  validateUADCompliance,
  generateUADXML,
  generateUADNarrativeSections
};